/**
 * Who a signup round is for.
 *
 * A round used to be offered to everybody, which was fine while the only way in was a link the
 * office sent to the right families. Pinning an open round to the parent dashboard removed that
 * filter, so the round has to carry its own audience.
 *
 * Both targets are optional and mean "no restriction" when absent, which is what every round
 * created before this behaved like. When both are set, a child has to satisfy both: the right
 * grade *and* an active enrollment in the right kind of programme — the second being what makes
 * them somebody's student to meet about in the first place.
 */

import { normalizeGrade } from './rosterGrouping';

export interface SignupRound {
  id: string;
  title: string;
  /** Last day of the round; a `DATE` column, so compared as a string, never parsed. */
  endDate?: string | null;
  /** `normalizeGrade` outputs — 'TK', 'K', '1'…'12'. Null/empty = every grade. */
  targetGrades?: string[] | null;
}

export interface EligibilityCandidate {
  id: string;
  firstName: string;
  active: boolean;
  /** Raw `students.metadata.grade`; normalized here, not by the caller. */
  grade?: string | null;
  /** `programs.type` for each of the child's *active* enrollments in an active program. */
  activeProgramTypes: string[];
}

/** The one enrollment type a conference round assumes. `programs.type`, not a display label. */
export const CONFERENCE_PROGRAM_TYPE = 'after-school';

function hasTargets(targets: string[] | null | undefined): targets is string[] {
  return Array.isArray(targets) && targets.length > 0;
}

/** One child against one round. Inactive children are never eligible. */
export function isEligibleForRound(candidate: EligibilityCandidate, round: SignupRound): boolean {
  if (!candidate.active) return false;

  if (hasTargets(round.targetGrades)) {
    const grade = normalizeGrade(candidate.grade);
    // A child whose grade nobody can read is not quietly included: an unreadable grade is a
    // data problem to fix, not a reason to invite a family to the wrong round.
    if (!grade || !round.targetGrades.includes(grade)) return false;
  }

  // Always required, and deliberately not a per-round setting.
  if (!candidate.activeProgramTypes.includes(CONFERENCE_PROGRAM_TYPE)) return false;

  return true;
}

export function eligibleStudents<T extends EligibilityCandidate>(candidates: T[], round: SignupRound): T[] {
  return candidates.filter(candidate => isEligibleForRound(candidate, round));
}

/**
 * Whether a round is still worth linking to.
 *
 * A family that has booked everybody still needs a way back in to move their time — right up
 * until the round is over, at which point the link is just clutter. Compared as date strings
 * because `end_date` is a `DATE`; parsing it would drag the viewer's timezone in and could
 * close the round a day early for anyone east of the school.
 */
export function isRoundOpen(round: SignupRound, todayInBusinessTZ: string): boolean {
  if (!round.endDate) return true;
  return round.endDate >= todayInBusinessTZ;
}

/** The grades a round can be aimed at, in school order. Matches `normalizeGrade` outputs. */
export const GRADE_OPTIONS: readonly string[] = [
  'TK', 'K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12',
] as const;
